/* eslint-disable camelcase -- Field rows use Bearsign API snake_case keys */

export const JOB_LEVEL_ALIAS_MAX_LENGTH = 5;
export const ARCHITECTURE_NAME_MAX_LENGTH = 100;

export const normalizeFieldRows = (fields) => fields.map((row) => {
  const is_hidden = row.is_hidden ?? false;
  const is_required = (row.is_required ?? false) && !is_hidden;

  return { ...row, is_hidden, is_required };
});

export const truncateShortName = (shortName) => (
  (shortName ?? '').slice(0, JOB_LEVEL_ALIAS_MAX_LENGTH)
);

export const normalizeLevelRows = (levels) => levels.map((row) => ({
  ...row,
  short_name: truncateShortName(row.short_name),
}));

const levelNameKey = (name) => name.trim().toLowerCase();

const LEVEL_NAME_MESSAGES = {
  required: 'Level name is required.',
  tooLong: `This name is too long. It needs to be ${ARCHITECTURE_NAME_MAX_LENGTH} characters or less.`,
  duplicate: 'Each level needs to have a unique name',
};

const getLevelNameIssue = (levels, name) => {
  const key = levelNameKey(name);

  if (!key) return 'required';
  if (key.length > ARCHITECTURE_NAME_MAX_LENGTH) return 'tooLong';
  if (levels.filter((level) => levelNameKey(level.name) === key).length > 1) return 'duplicate';

  return null;
};

export const getLevelNameErrors = (levels) => {
  const errors = {};

  levels.forEach((row) => {
    const issue = getLevelNameIssue(levels, row.name);

    if (issue) {
      errors[row.rowKey] = LEVEL_NAME_MESSAGES[issue];
    }
  });

  return errors;
};

export const isLevelNameDuplicateError = (levels, rowKey) => {
  const row = levels.find((level) => level.rowKey === rowKey);

  if (!row) return false;

  return getLevelNameIssue(levels, row.name) === 'duplicate';
};

const architectureNameKey = (name) => name.trim().toLowerCase();

export const JOB_FAMILY_NAME_DUPLICATE_MESSAGE = 'Each family needs to have a unique name';
export const JOB_FAMILY_GROUP_NAME_DUPLICATE_MESSAGE = 'Each family group needs to have a unique name';

const architectureNameTooLongMessage = (
  `This name is too long. It needs to be ${ARCHITECTURE_NAME_MAX_LENGTH} characters or less.`
);

export const getArchitectureNameError = (name, existingNames, duplicateMessage) => {
  const trimmed = name.trim();

  if (!trimmed) {
    return null;
  }

  if (trimmed.length > ARCHITECTURE_NAME_MAX_LENGTH) {
    return architectureNameTooLongMessage;
  }

  const key = architectureNameKey(trimmed);

  if (existingNames.some((existing) => architectureNameKey(existing) === key)) {
    return duplicateMessage;
  }

  return null;
};

export const isArchitectureNameValid = (name, existingNames, duplicateMessage) => (
  Boolean(name.trim()) && !getArchitectureNameError(name, existingNames, duplicateMessage)
);

export const withOrderIndex = (rows) => rows.map((row, index) => ({
  ...row,
  order: index,
}));

export const canSaveSettings = ({ fields, levels }) => {
  const normalizedFields = normalizeFieldRows(fields);
  const hasVisibleField = normalizedFields.length === 0
    || normalizedFields.some((field) => !field.is_hidden);

  return hasVisibleField && Object.keys(getLevelNameErrors(levels)).length === 0;
};
